import React from 'react';
import { FiDatabase, FiFileText, FiSearch, FiX, FiZap } from 'react-icons/fi';
import { Button } from '../../ui/button';
import { Card, CardContent } from '../../ui/card';
import { Input } from '../../ui/input';
import IconWrapper from '../../ui/IconWrapper';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import type { FOSDecisionsResponse } from '../../../hooks/useFOSAdmin';
import {
  formatDate,
  getDecisionOutcomeColor,
  getProcessingStatusInfo,
} from './fosAdminDashboardUtils';

interface FOSAdminBrowseTabProps {
  searchQuery: string;
  processingStatusFilter: string;
  decisionStatusFilter: string;
  industrySectorFilter: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  hasActiveFilters: string;
  totalBrowseItems: number;
  browsePage: number;
  totalBrowsePages: number;
  decisionsData?: FOSDecisionsResponse;
  decisionsLoading: boolean;
  reprocessSinglePending: boolean;
  onSearchQueryChange: (value: string) => void;
  onProcessingStatusFilterChange: (value: string) => void;
  onDecisionStatusFilterChange: (value: string) => void;
  onIndustrySectorFilterChange: (value: string) => void;
  onSortByChange: (value: string) => void;
  onSortOrderChange: (value: 'asc' | 'desc') => void;
  onClearFilters: () => void;
  onBrowsePageChange: (updater: (page: number) => number) => void;
  onSelectDecision: (decisionId: string) => void;
  onProcessSingle: (decisionId: string) => void;
}

const FOSAdminBrowseTab: React.FC<FOSAdminBrowseTabProps> = ({
  searchQuery,
  processingStatusFilter,
  decisionStatusFilter,
  industrySectorFilter,
  sortBy,
  sortOrder,
  hasActiveFilters,
  totalBrowseItems,
  browsePage,
  totalBrowsePages,
  decisionsData,
  decisionsLoading,
  reprocessSinglePending,
  onSearchQueryChange,
  onProcessingStatusFilterChange,
  onDecisionStatusFilterChange,
  onIndustrySectorFilterChange,
  onSortByChange,
  onSortOrderChange,
  onClearFilters,
  onBrowsePageChange,
  onSelectDecision,
  onProcessSingle,
}) => (
  <div className="space-y-6">
    <Card>
      <CardContent className="pt-6">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="relative flex-1">
            <IconWrapper icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by business name or reference..."
              value={searchQuery}
              onChange={(e) => onSearchQueryChange(e.target.value)}
              className="pl-10"
            />
          </div>

          <Select value={processingStatusFilter} onValueChange={onProcessingStatusFilterChange}>
            <SelectTrigger className="w-full lg:w-[160px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {decisionsData?.filters?.processing_statuses?.map((status) => (
                <SelectItem key={status} value={status}>{status}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={decisionStatusFilter} onValueChange={onDecisionStatusFilterChange}>
            <SelectTrigger className="w-full lg:w-[160px]">
              <SelectValue placeholder="Outcome" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Outcomes</SelectItem>
              {decisionsData?.filters?.decision_statuses?.map((status) => (
                <SelectItem key={status} value={status}>{status}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={industrySectorFilter} onValueChange={onIndustrySectorFilterChange}>
            <SelectTrigger className="w-full lg:w-[160px]">
              <SelectValue placeholder="Sector" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sectors</SelectItem>
              {decisionsData?.filters?.industry_sectors?.map((sector) => (
                <SelectItem key={sector} value={sector}>{sector}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={onClearFilters}>
              <IconWrapper icon={FiX} className="h-4 w-4 mr-1" />
              Clear
            </Button>
          )}
        </div>

        <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
          <span>
            {totalBrowseItems.toLocaleString()} decisions{hasActiveFilters && ' (filtered)'}
          </span>
          <div className="flex items-center gap-2">
            <span>Sort:</span>
            <Select value={sortBy} onValueChange={onSortByChange}>
              <SelectTrigger className="w-[140px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="decision_date">Decision Date</SelectItem>
                <SelectItem value="business_name">Business Name</SelectItem>
                <SelectItem value="reference">Reference</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="ghost" size="sm" onClick={() => onSortOrderChange(sortOrder === 'asc' ? 'desc' : 'asc')}>
              {sortOrder === 'asc' ? '↑' : '↓'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>

    <Card>
      <CardContent className="pt-6">
        {decisionsLoading ? (
          <div className="flex items-center justify-center py-12 text-gray-500">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3" />
            Loading decisions...
          </div>
        ) : decisionsData?.decisions && decisionsData.decisions.length > 0 ? (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reference</TableHead>
                  <TableHead>Business</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Outcome</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {decisionsData.decisions.map((decision) => {
                  const statusInfo = getProcessingStatusInfo(decision.processing_status);
                  return (
                    <TableRow key={decision.id}>
                      <TableCell className="font-medium">{decision.reference}</TableCell>
                      <TableCell className="max-w-[180px] truncate" title={decision.business_name ?? undefined}>
                        {decision.business_name || '-'}
                      </TableCell>
                      <TableCell className="text-gray-600">{formatDate(decision.decision_date)}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getDecisionOutcomeColor(decision.decision_status)}`}>
                          {decision.decision_status}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusInfo.color}`}>
                          {statusInfo.label}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onSelectDecision(decision.id)}
                          >
                            <IconWrapper icon={FiFileText} className="h-4 w-4" />
                          </Button>
                          {statusInfo.canProcess && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onProcessSingle(decision.id)}
                              disabled={reprocessSinglePending}
                              title="Process this decision"
                            >
                              <IconWrapper icon={FiZap} className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                Page {browsePage} of {totalBrowsePages.toLocaleString()}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onBrowsePageChange((page) => Math.max(1, page - 1))}
                  disabled={browsePage === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onBrowsePageChange((page) => Math.min(totalBrowsePages, page + 1))}
                  disabled={browsePage >= totalBrowsePages}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <IconWrapper icon={FiDatabase} className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium">No decisions found</p>
            <p className="mt-1">Try adjusting your filters</p>
          </div>
        )}
      </CardContent>
    </Card>
  </div>
);

export default FOSAdminBrowseTab;
